import React, { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { ThemeProvider } from './contexts/ThemeContext';
import { logConfig } from './utils/config';
import NewDesignDemo from './v2/pages/NewDesignDemo';
import DashboardV2 from './v2/pages/Dashboard';
import FolderPage from './v2/pages/FolderPage';
import ItemsPage from './v2/pages/ItemsPage';
import TrashPage from './v2/pages/TrashPage';
import PopupDemo from './v2/pages/demo/PopupDemo';
import SignUpPage from './v2/pages/SignUpPage';
import SignInPage from './v2/pages/SignInPage';
import ShortCodeRequestPage from './v2/pages/ShortCodeRequestPage';
import ShortCodeVerifyPage from './v2/pages/ShortCodeVerifyPage';
import PasswordResetRequestPage from './v2/pages/PasswordResetRequestPage';
import PasswordResetPage from './v2/pages/PasswordResetPage';
import EmailChangeConfirmCurrentPage from './v2/pages/EmailChangeConfirmCurrentPage';
import EmailChangeConfirmNewPage from './v2/pages/EmailChangeConfirmNewPage';
import SettingsPage from './v2/pages/SettingsPage';
import LandingPage from './v2/pages/LandingPage';
import PrivacyPage from './v2/pages/PrivacyPage';

function App() {
  const { isAuthenticated, isCheckingAuth } = useAuthStore();

  useEffect(() => {
    // Log configuration on app start
    logConfig();
  }, []);

  console.log('App render - isAuthenticated:', isAuthenticated, 'isCheckingAuth:', isCheckingAuth);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {isCheckingAuth ? (
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <Routes>
            {/* Public routes */}
            <Route path="/sign-up" element={!isAuthenticated ? <SignUpPage /> : <Navigate to="/my" replace />} />
            <Route path="/sign-in" element={!isAuthenticated ? <SignInPage /> : <Navigate to="/my" replace />} />
            <Route path="/short-code-request" element={!isAuthenticated ? <ShortCodeRequestPage /> : <Navigate to="/my" replace />} />
            <Route path="/short-code-verify" element={!isAuthenticated ? <ShortCodeVerifyPage /> : <Navigate to="/my" replace />} />
            <Route path="/password-reset-request" element={!isAuthenticated ? <PasswordResetRequestPage /> : <Navigate to="/my" replace />} />
            <Route path="/password-reset" element={!isAuthenticated ? <PasswordResetPage /> : <Navigate to="/my" replace />} />
            <Route path="/email-change/confirm-current" element={<EmailChangeConfirmCurrentPage />} />
            <Route path="/email-change/confirm-new" element={<EmailChangeConfirmNewPage />} />

            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Protected routes */}
            <Route path="/my" element={isAuthenticated ? <Outlet /> : <Navigate to="/sign-in" replace />}>
              <Route index element={<DashboardV2 />} />
              <Route path="items" element={<ItemsPage />} />
              <Route path="trash" element={<TrashPage />} />
              <Route path="folder/:clipboardId" element={<FolderPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="new-design-demo" element={<NewDesignDemo />} />
              <Route path="popup-demo" element={<PopupDemo />} />
            </Route>

            {/* Fallback - only for truly unmatched routes */}
            <Route path="*" element={isAuthenticated ? <Navigate to="/my" replace /> : <Navigate to="/sign-in" replace />} />
          </Routes>
        )}
      </div>
      {process.env.NODE_ENV !== 'production' ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App; 